import React from 'react';
import { Card } from '../../ui/Card';
import { motion } from 'framer-motion';
import { LightbulbIcon, CheckCircleIcon, AlertTriangleIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export interface InsightItem {
  id: string;
  type: 'insight' | 'opportunity';
  priority?: 'high' | 'medium' | 'low';
  icon?: 'trend-up' | 'trend-down' | 'warning' | 'check' | 'chart';
  text: string;
  subtext: string;
}
interface InsightCardProps {
  title: string;
  items: InsightItem[];
  variant?: 'insights' | 'opportunities';
  delay?: number;
}
export function InsightCard({
  title,
  items,
  variant = 'insights',
  delay = 0
}: InsightCardProps) {
  const getIcon = (iconType?: string) => {
    switch (iconType) {
      case 'trend-up':
        return <TrendingUpIcon className="w-5 h-5 text-red-500" data-id="element-135" />;
      case 'trend-down':
        return <TrendingDownIcon className="w-5 h-5 text-accent-success" data-id="element-136" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5 text-yellow-500" data-id="element-137" />;
      case 'check':
        return <CheckCircleIcon className="w-5 h-5 text-accent-success" data-id="element-138" />;
      case 'chart':
        return <TrendingUpIcon className="w-5 h-5 text-accent-primary" data-id="element-139" />;
      default:
        return <LightbulbIcon className="w-5 h-5 text-accent-primary" data-id="element-140" />;
    }
  };
  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;
    const styles = {
      high: 'bg-red-500/10 text-red-500 border-red-500/20',
      medium: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      low: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    };
    const style = styles[priority as keyof typeof styles] || styles.low;
    return <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${style}`} data-id="element-141">
        {priority}
      </span>;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay
  }} className="h-full" data-id="element-142">
      <Card className="h-full flex flex-col" data-id="element-143">
        <div className="flex items-center gap-2 mb-6 border-b border-white/5 pb-4" data-id="element-144">
          {variant === 'insights' ? <LightbulbIcon className="w-5 h-5 text-yellow-500" data-id="element-145" /> : <CheckCircleIcon className="w-5 h-5 text-accent-success" data-id="element-146" />}
          <h3 className="font-bold text-lg text-white" data-id="element-147">{title}</h3>
        </div>

        <div className="space-y-6 flex-1" data-id="element-148">
          {items.map(item => <div key={item.id} className="flex gap-4" data-id="element-149">
              <div className="mt-1 flex-shrink-0" data-id="element-150">{getIcon(item.icon)}</div>
              <div className="flex-1" data-id="element-151">
                <div className="flex items-center gap-2 mb-1" data-id="element-152">
                  {getPriorityBadge(item.priority)}
                  <p className="font-medium text-white text-sm" data-id="element-153">{item.text}</p>
                </div>
                <p className="text-sm text-text-secondary" data-id="element-154">{item.subtext}</p>
              </div>
            </div>)}
        </div>
      </Card>
    </motion.div>;
}